import { Injectable } from "@angular/core";
import { Observable, map } from "rxjs";
import {
  ConfirmedInstruction,
  Instruction,
  InstructionProgress,
  InstructionType,
  OpenInstruction,
} from "../models/instruction/instruction";
import { InstructionConfirmation } from "../models/instruction/instruction-confirmation";
import {
  FrappeApiHelper,
  FrappeRequestListOptions,
  mappingBuilder,
  mappingBuilderWithDefaultMappings,
  mappingHelper,
} from "@aht/frappe-client";

@Injectable({
  providedIn: "root",
})
export class InstructionService {
  private instructionMapping = mappingBuilderWithDefaultMappings<Instruction>(
    "SAR Instruction",
    [
      mappingHelper.stringMapper("title"),
      mappingHelper.stringMapper("description"),
      mappingHelper.dateMapper("valid_from", "validFrom"),
      mappingHelper.optionalDateMapper("valid_until", "validUntil"),
      mappingHelper.stringMapper("type"),
      mappingHelper.stringMapper("mode"),
    ],
  );

  private instructionConfirmationMapping =
    mappingBuilderWithDefaultMappings<InstructionConfirmation>(
      "SAR Instruction Confirmation",
      [
        mappingHelper.stringMapper("user"),
        mappingHelper.stringMapper("instruction"),
        mappingHelper.booleanMapper("read"),
        mappingHelper.docStatusMapper("docstatus", "status"),
        mappingHelper.optionalDateMapper("deadline", "deadline"),
      ],
    );

  private instructionProgressMapping = mappingBuilder<InstructionProgress>(
    "DOE SNOT MATTER",
    [
      mappingHelper.intMapper("confirmed_chapters", "confirmedChapters"),
      mappingHelper.intMapper("total_chapters", "totalChapters"),
    ],
  );

  private confirmedInstructionMapper = mappingBuilder<ConfirmedInstruction>(
    "DOES NOT MATTER",
    [
      mappingHelper.objectMapper(
        this.instructionConfirmationMapping.conversion(),
        "confirmation",
      ),
      mappingHelper.objectMapper(
        this.instructionMapping.conversion(),
        "instruction",
      ),
      mappingHelper.objectMapper(
        this.instructionProgressMapping.conversion(),
        "progress",
      ),
    ],
  );

  private openInstructionMapper = mappingBuilder<OpenInstruction>(
    "DOES NOT MATTER",
    [
      mappingHelper.objectMapper(
        this.instructionConfirmationMapping.conversion(),
        "confirmation",
      ),
      mappingHelper.objectMapper(
        this.instructionMapping.conversion(),
        "instruction",
      ),
      mappingHelper.objectMapper(
        this.instructionProgressMapping.conversion(),
        "progress",
      ),
    ],
  );

  private instructionDtoMapper = mappingBuilder<{
    confirmed: ConfirmedInstruction[];
    open: OpenInstruction[];
  }>("DOES NOT MATTER", [
    mappingHelper.listMapperWithItemConversion(
      this.confirmedInstructionMapper.conversion(),
      "confirmed",
    ),
    mappingHelper.listMapperWithItemConversion(
      this.openInstructionMapper.conversion(),
      "open",
    ),
  ]);

  constructor(private readonly helper: FrappeApiHelper) {}

  private sortByValidFromDate(instructions: Instruction[]): Instruction[] {
    return [...instructions].sort(
      (a, b) => b.validFrom.valueOf() - a.validFrom.valueOf(),
    );
  }
  /*
   * XXX: Attention!!!
   *
   * Because of the fact that the admin and project leads also use the same frontend whilst using the backend, we can't use the default get_list methods
   * and have to use custom methods where we use get_all in the backend and filter based on the user.
   */

  loadMyOpenInstructions(
    instructionType: InstructionType,
  ): Observable<Instruction[]> {
    const params = new Map<string, string>();
    params.set("instruction_type", instructionType);
    return this.helper
      .callWithResults(this.instructionMapping.conversion(), {
        method: "sar.controllers.instruction_controller.my_open_instructions",
        type: "GET",
        extraParams: params,
      })
      .pipe(map(this.sortByValidFromDate));
  }

  // TODO: unused
  loadMyConfirmedInstructions(
    instructionType: InstructionType,
  ): Observable<Instruction[]> {
    const params = new Map<string, string>();
    params.set("instruction_type", instructionType);
    return this.helper
      .callWithResults(this.instructionMapping.conversion(), {
        method:
          "sar.controllers.instruction_controller.my_confirmed_instructions",
        type: "GET",
        extraParams: params,
      })
      .pipe(map(this.sortByValidFromDate));
  }

  loadMyInstruction(name: string): Observable<Instruction> {
    const extraParams = new Map<string, string>();
    extraParams.set("name", name);

    return this.helper.callWithResult(this.instructionMapping.conversion(), {
      method: "sar.controllers.instruction_controller.my_instruction",
      extraParams: extraParams,
      type: "GET",
    });
  }

  loadMyInstructionConfirmation(
    instructionConfirmation: string,
  ): Observable<InstructionConfirmation | undefined> {
    const extraParams = new Map<string, string>();
    extraParams.set("instruction_confirmation", instructionConfirmation);

    return this.helper.callWithOptionalResult(
      this.instructionConfirmationMapping.conversion(),
      {
        method:
          "sar.controllers.instruction_controller.my_instruction_confirmation",
        extraParams: extraParams,
        type: "GET",
      },
    );
  }

  // TODO: unused
  loadInstructionConfirmations(): Observable<InstructionConfirmation[]> {
    return this.helper.getList(this.instructionConfirmationMapping);
  }

  loadInstructions(instructionType: InstructionType | "All"): Observable<{
    confirmed: ConfirmedInstruction[];
    open: OpenInstruction[];
  }> {
    const params = new Map<string, string>();
    params.set("instruction_type", instructionType);
    return this.helper
      .callWithResult(this.instructionDtoMapper.conversion(), {
        method: "sar.controllers.instruction_controller.instructions",
        type: "GET",
        extraParams: params,
      })
      .pipe(
        map((data) => ({
          confirmed: [...data.confirmed].sort(
            (a, b) =>
              b.confirmation.creation.getTime() -
              a.confirmation.creation.getTime(),
          ),
          open: [...data.open].sort(
            (a, b) =>
              b.confirmation.creation.getTime() -
              a.confirmation.creation.getTime(),
          ),
        })),
      );
  }

  markAsRead(name: string): Observable<InstructionConfirmation> {
    return this.helper.callWithResult(
      this.instructionConfirmationMapping.conversion(),
      {
        method: "sar.controllers.instruction_controller.mark_as_read",
        type: "POST",
        body: {
          instruction_confirmation: name,
        },
      },
    );
  }
}
